import { memo, useCallback, useMemo } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { QueryRule as QueryRuleType, QueryField, OperatorValue } from '../../../../types/QueryBuilder';
import { getFieldsByCategory, getOperatorsForField } from '../../../../utils/queryBuilderUtils';
import { useLanguage } from '../../../../context/LanguageContext';
import ValueEditor from './ValueEditors';

interface QueryRuleProps {
    rule: QueryRuleType;
    categories: string[];
    fields: QueryField[];
    onUpdateRule: (ruleId: string, data: Partial<QueryRuleType>) => void;
    onRemoveRule: (ruleId: string) => void;
}

const QueryRule = memo(function QueryRule({
    rule,
    categories,
    fields,
    onUpdateRule,
    onRemoveRule,
}: QueryRuleProps) {
    const { t } = useLanguage();
    const ruleId = rule.id;
    const field = useMemo(
        () => fields.find(f => f.name === rule.field && f.Categorie === rule.category),
        [fields, rule.field, rule.category]
    );
    const availableFields = useMemo(
        () => rule.category ? getFieldsByCategory(fields, rule.category) : [],
        [fields, rule.category]
    );
    const operators = useMemo(() => getOperatorsForField(field), [field]);

    const handleCategoryChange = useCallback((category: string) => {
        const newFields = getFieldsByCategory(fields, category);
        const newField = newFields[0]?.name || '';
        onUpdateRule(ruleId, {
            category,
            field: newField,
            operator: '=',
            value: '',
            valueTo: undefined
        });
    }, [fields, onUpdateRule, ruleId]);

    const handleFieldChange = useCallback((fieldName: string) => {
        const newField = fields.find(f => f.name === fieldName && f.Categorie === rule.category);
        onUpdateRule(ruleId, {
            field: fieldName,
            operator: (newField?.defaultOperator as OperatorValue) || '=',
            value: '',
            valueTo: undefined
        });
    }, [fields, rule.category, onUpdateRule, ruleId]);

    const handleOperatorChange = useCallback((operator: OperatorValue) => {
        onUpdateRule(ruleId, {
            operator,
            ...(operator !== 'between' ? { valueTo: undefined } : {})
        });
    }, [onUpdateRule, ruleId]);

    const handleValueChange = useCallback(
        (value: QueryRuleType['value']) => onUpdateRule(ruleId, { value }),
        [onUpdateRule, ruleId]
    );
    const handleRemove = useCallback(() => onRemoveRule(ruleId), [onRemoveRule, ruleId]);

    const renderValueEditor = () => {
        if (!field) return null;

        return (
            <Box sx={{ flexGrow: 1, minWidth: '200px', maxWidth: 'calc(100% - 565px)' }}>
                <ValueEditor
                    ruleId={rule.id}
                    field={field}
                    operator={rule.operator || '='}
                    value={rule.value}
                    onChange={handleValueChange}
                />
            </Box>
        );
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                p: 1.5,
                bgcolor: '#ffffff',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                '&:hover': {
                    borderColor: '#556ee6',
                    boxShadow: '0 2px 8px rgba(85, 110, 230, 0.1)'
                }
            }}
        >
            <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('pages.clients.queryBuilder.category')}</InputLabel>
                <Select
                    value={rule.category || ''}
                    label={t('pages.clients.queryBuilder.category')}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                            },
                        },
                    }}
                >
                    {categories.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                            {cat}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t('pages.clients.queryBuilder.field')}</InputLabel>
                <Select
                    value={rule.field || ''}
                    label={t('pages.clients.queryBuilder.field')}
                    onChange={(e) => handleFieldChange(e.target.value)}
                    disabled={!rule.category}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                            },
                        },
                    }}
                >
                    {availableFields.map((f) => (
                        <MenuItem key={f.name} value={f.name}>
                            {f.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>{t('pages.clients.queryBuilder.operator')}</InputLabel>
                <Select
                    value={rule.operator || ''}
                    label={t('pages.clients.queryBuilder.operator')}
                    onChange={(e) => handleOperatorChange(e.target.value as OperatorValue)}
                    disabled={!rule.field}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                            },
                        },
                    }}
                >
                    {operators.map((op) => (
                        <MenuItem key={op.value} value={op.value}>
                            {op.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {rule.field && rule.operator && renderValueEditor()}

            <Tooltip title={t('pages.clients.queryBuilder.deleteRule')}>
                <IconButton
                    size="small"
                    onClick={handleRemove}
                    sx={{
                        color: '#dc3545',
                        '&:hover': {
                            backgroundColor: 'rgba(220, 53, 69, 0.1)'
                        }
                    }}
                >
                    <DeleteOutlineIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );
});

export default QueryRule;
